import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCommercialInvoices, type CommercialInvoice, type InvoiceItemDB } from '../../../hooks/useCommercialInvoices'
import { round2, ciLineTotals, packagesNote } from '../../../lib/ciNormalise'
import { useAuth } from '../../../contexts/AuthContext'
import { hasAnyRole } from '../../../lib/roles'
import { generateCDSExcel } from '../../../api/cdsExporter'
import { isValidIsoCountry } from '../../../lib/cds/countries'

const STATUS_OPTIONS = ['draft', 'confirmed', 'filed'] as const

// Z98: weights/amount are saved as the sum of the 2 dp lines; when that differs from what the invoice itself
// printed, say so next to the figure (the printed value is kept in stated_*).
function statedNote(stated: number | null | undefined, declared: number | null | undefined): string {
  if (stated == null || declared == null) return ''
  return Number(stated) === Number(declared) ? '' : ` (invoice: ${Number(stated)})`
}

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { getInvoiceWithItems, updateInvoice, updateItemHSCode, updateInvoiceItem, deleteInvoice } = useCommercialInvoices()
  const { userRoles } = useAuth()
  const canDelete = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])
  const [invoice, setInvoice] = useState<CommercialInvoice | null>(null)
  const [items, setItems] = useState<InvoiceItemDB[]>([])
  const [loading, setLoading] = useState(true)
  const [editingHS, setEditingHS] = useState<string | null>(null)
  const [hsInput, setHsInput] = useState('')
  // Z42: row-level edit mode for everything HS-code editing didn't cover (description,
  // quantities, weights, amount, origin) -- see updateInvoiceItem in useCommercialInvoices.
  const [editingRow, setEditingRow] = useState<string | null>(null)
  const [rowDraft, setRowDraft] = useState<Partial<InvoiceItemDB>>({})
  const [rowSaving, setRowSaving] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [useCyukDan, setUseCyukDan] = useState(false)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    if (!id) return
    getInvoiceWithItems(id).then(({ invoice: inv, items: its }) => {
      setInvoice(inv)
      setItems(its)
      setLoading(false)
    })
  }, [id, getInvoiceWithItems])

  // Calculate pivot table grouped by HS code
  const pivot = useMemo(() => {
    if (!items) return []
    const map = new Map<string, any>()
    for (const item of items) {
      const key = item.hs_code || 'UNKNOWN'
      if (!map.has(key)) {
        map.set(key, {
          hs_code: key,
          goods_description: item.description || '',
          pcs: 0,
          ctns: 0,
          gross_weight: 0,
          net_weight: 0,
          amount: 0,
        })
      }
      const p = map.get(key)!
      p.pcs += item.pcs || 0
      p.ctns += item.ctns || 0
      p.gross_weight += item.gross_weight_kg || 0
      p.net_weight += item.net_weight_kg || 0
      p.amount += item.total_amount || 0
    }
    return Array.from(map.values())
  }, [items])

  async function handleDelete() {
    if (!id || !invoice) return
    const confirmed = window.confirm(
      `Are you sure you want to delete invoice ${invoice.invoice_no ?? id}? This cannot be undone.`
    )
    if (!confirmed) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteInvoice(id)
      navigate('/customs/invoices')
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete invoice')
      setDeleting(false)
    }
  }

  async function handleStatusChange(status: typeof STATUS_OPTIONS[number]) {
    if (!id) return
    setSaving(true)
    await updateInvoice(id, { status })
    setInvoice(prev => prev ? { ...prev, status } : prev)
    setSaving(false)
  }

  async function handleHSSave(itemId: string) {
    await updateItemHSCode(itemId, hsInput, items.find(it => it.id === itemId)?.description ?? '')
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, hs_code: hsInput, hs_code_confirmed: true } : it))
    setEditingHS(null)
  }

  function startEditRow(item: InvoiceItemDB) {
    setEditingRow(item.id)
    setRowDraft({
      description: item.description,
      pcs: item.pcs,
      ctns: item.ctns,
      gross_weight_kg: item.gross_weight_kg,
      net_weight_kg: item.net_weight_kg,
      unit_value: item.unit_value,
      total_amount: item.total_amount,
      country_of_origin: item.country_of_origin,
    })
  }

  async function saveEditRow(itemId: string) {
    setRowSaving(true)
    try {
      // Z98: 2 dp per line; a manual CTNS edit clears the auto-adjusted marker (colour back to normal);
      // a 0 is never stored (-> 1, original 0 kept). Header totals are then re-summed from the rounded lines.
      const before = items.find(it => it.id === itemId)
      const draft: Partial<InvoiceItemDB> = { ...rowDraft }
      if ('gross_weight_kg' in draft) draft.gross_weight_kg = round2(draft.gross_weight_kg)
      if ('net_weight_kg' in draft) draft.net_weight_kg = round2(draft.net_weight_kg)
      if ('total_amount' in draft) draft.total_amount = round2(draft.total_amount)
      if (before && draft.ctns !== before.ctns) draft.ctns_original = null
      if (draft.ctns === 0) { draft.ctns = 1; draft.ctns_original = 0 }
      await updateInvoiceItem(itemId, draft)
      const nextItems = items.map(it => it.id === itemId ? { ...it, ...draft } : it)
      const t = ciLineTotals(nextItems.map(it => ({ amount: it.total_amount, gross_weight: it.gross_weight_kg, net_weight: it.net_weight_kg, ctns: it.ctns })) as any)
      await updateInvoice(id!, { total_amount: t.total_amount, gross_weight_kg: t.gross_weight_kg, net_weight_kg: t.net_weight_kg })
      setInvoice(prev => prev ? { ...prev, total_amount: t.total_amount, gross_weight_kg: t.gross_weight_kg, net_weight_kg: t.net_weight_kg } : prev)
      setItems(nextItems)
      setEditingRow(null)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to save line item')
    } finally {
      setRowSaving(false)
    }
  }

  function handleExportCDS() {
    if (!invoice) return
    setExporting(true)
    try {
      // Convert invoice data to InvoiceHeader format
      const header = {
        invoice_no: invoice.invoice_no,
        job_ref: invoice.job_ref,
        bill_no: invoice.bill_no,
        declare_method: null,
        trade_term: null,
        shipper: invoice.shipper,
        shipper_address: null,
        consignee: invoice.consignee,
        consignee_address: invoice.consignee_address,
        country_of_origin: invoice.country_of_origin,
        destination_country: invoice.destination_country,
        invoice_date: invoice.invoice_date,
        currency: invoice.currency,
        total_amount: invoice.total_amount,
        gross_weight_kg: invoice.gross_weight_kg,
        net_weight_kg: invoice.net_weight_kg,
        packages: invoice.packages,
        incoterms: invoice.incoterms,
        vessel_flight: invoice.vessel_flight,
        port_of_loading: invoice.port_of_loading,
        port_of_discharge: invoice.port_of_discharge,
        vat_deferred: invoice.vat_deferred,
        total_cbm: null,
        use_cyuk_dan: false,
        eori: null,
        vat_no: null
      }
      generateCDSExcel(header, pivot, useCyukDan)
    } catch (err) {
      console.error('Error exporting CDS Excel:', err)
    } finally {
      setExporting(false)
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>
  if (!invoice) return <div className="min-h-screen flex items-center justify-center"><div className="text-red-500">Invoice not found</div></div>

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/customs/invoices')} className="text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {invoice.invoice_no ?? 'Invoice Detail'}
          </h1>
          <div className="ml-auto flex items-center gap-3">
            {invoice.source_type === 'pdf' && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded-full text-sm">
                AI {invoice.ai_confidence != null ? `${Math.round(invoice.ai_confidence * 100)}%` : 'Parsed'}
              </span>
            )}
            <select
              value={invoice.status}
              onChange={e => handleStatusChange(e.target.value as typeof STATUS_OPTIONS[number])}
              disabled={saving}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            {items.length > 0 && (
              <button
                onClick={handleExportCDS}
                disabled={exporting || pivot.length === 0}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {exporting ? 'Exporting...' : 'Export CDS'}
              </button>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </div>

        {deleteError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {deleteError}
          </div>
        )}

        {/* Header fields */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Invoice Header</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {[
              ['Invoice No', invoice.invoice_no],
              ['Shipper', invoice.shipper],
              ['Consignee', invoice.consignee],
              ['Date', invoice.invoice_date],
              ['Currency', invoice.currency],
              ['Total Amount', invoice.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + statedNote(invoice.stated_total_amount, invoice.total_amount)],
              ['Gross Weight (kg)', invoice.gross_weight_kg?.toFixed(2) + statedNote(invoice.stated_gross_weight_kg, invoice.gross_weight_kg)],
              ['Net Weight (kg)', invoice.net_weight_kg?.toFixed(2) + statedNote(invoice.stated_net_weight_kg, invoice.net_weight_kg)],
              ['Packages', invoice.packages?.toString()],
              ['Incoterms', invoice.incoterms],
              ['Port of Loading', invoice.port_of_loading],
              ['Port of Discharge', invoice.port_of_discharge],
              ['Origin Country', invoice.country_of_origin],
              ['Vessel/Flight', invoice.vessel_flight],
              ['Source File', invoice.source_file_name],
              ['Uploaded', new Date(invoice.created_at).toLocaleDateString()],
            ].map(([label, val]) => {
              const isTruncatable = (label as string) === 'Consignee' || (label as string) === 'Shipper'
              return (
                <div key={label as string}>
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">{label as string}</p>
                  <p
                    className={`text-gray-900 dark:text-white font-medium mt-1 ${isTruncatable ? 'truncate' : ''}`}
                    title={isTruncatable && val ? (val as string) : undefined}
                  >
                    {(label as string) === 'Packages' ? (
                      // Editable here because the declaration refuses to generate without the invoice's printed total
                      // package count, and an invoice saved without one would otherwise be stuck. This is the figure
                      // printed on the invoice -- NEVER the sum of the line CTNS (see src/lib/ciNormalise.ts).
                      <input
                        type="number" min={0} defaultValue={invoice.packages ?? ''}
                        placeholder="total packages"
                        className="w-28 rounded border border-gray-300 px-2 py-0.5 text-sm dark:border-gray-600 dark:bg-gray-800"
                        onBlur={async e => {
                          const v = e.target.value === '' ? null : Math.max(0, Math.round(Number(e.target.value)))
                          if (v === (invoice.packages ?? null)) return
                          try { await updateInvoice(id!, { packages: v }); setInvoice(prev => prev ? { ...prev, packages: v } : prev) }
                          catch (err) { alert(err instanceof Error ? err.message : 'Failed to save packages') }
                        }}
                      />
                    ) : (label as string) === 'Origin Country' && val && !isValidIsoCountry(val as string)
                      ? <span className="text-red-600 dark:text-red-400" title="Not a valid 2-letter country code — CDS export will be blocked until corrected">{val} ⚠</span>
                      : (val ?? '—')}
                  </p>
                </div>
              )
            })}
          </div>
          {invoice.consignee_address && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">Consignee Address</p>
              <p className="text-gray-900 dark:text-white text-sm mt-1">{invoice.consignee_address}</p>
            </div>
          )}
        </div>

        {/* Z98: plain information, NOT a warning. The invoice's total package count is what is declared; the
            lines are each at least 1 (0 / part cartons -> 1), so their sum is normally higher. See ciNormalise.ts. */}
        {(() => {
          const note = invoice ? packagesNote(invoice.packages, items) : null
          if (!note) return null
          return (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-700 dark:text-gray-300">
              ℹ {note}
            </div>
          )
        })()}

        {/* Line items with editable HS codes */}
        {items.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Line Items ({items.length})</h2>
              <p className="text-xs text-gray-500">Click HS code to edit inline, or "Edit" to correct other fields</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-300">#</th>
                    <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-300">Description</th>
                    <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-300">HS Code</th>
                    <th className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">Pcs / Ctns</th>
                    <th className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">Gross Weight</th>
                    <th className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">Amount</th>
                    <th className="px-4 py-2 text-center text-gray-600 dark:text-gray-300">Origin</th>
                    <th className="px-4 py-2 text-center text-gray-600 dark:text-gray-300"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {items.map((item) => {
                    const isEditingRow = editingRow === item.id
                    return isEditingRow ? (
                      <tr key={item.id} className="bg-blue-50 dark:bg-blue-900/10">
                        <td className="px-4 py-3 text-gray-500">{item.line_no ?? '—'}</td>
                        <td className="px-4 py-3">
                          <input
                            value={rowDraft.description ?? ''}
                            onChange={e => setRowDraft(d => ({ ...d, description: e.target.value }))}
                            className="w-full px-2 py-1 border border-blue-400 rounded text-sm bg-white dark:bg-gray-800"
                          />
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-gray-500">{item.hs_code ?? 'Add HS'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="number"
                              value={rowDraft.pcs ?? ''}
                              onChange={e => setRowDraft(d => ({ ...d, pcs: e.target.value === '' ? null : Number(e.target.value) }))}
                              className="w-16 px-2 py-1 border border-blue-400 rounded text-sm text-right bg-white dark:bg-gray-800"
                              placeholder="pcs"
                            />
                            <span className="text-gray-400">/</span>
                            <input
                              type="number"
                              value={rowDraft.ctns ?? ''}
                              onChange={e => setRowDraft(d => ({ ...d, ctns: e.target.value === '' ? null : Number(e.target.value) }))}
                              className="w-16 px-2 py-1 border border-blue-400 rounded text-sm text-right bg-white dark:bg-gray-800"
                              placeholder="ctns"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="number"
                              value={rowDraft.gross_weight_kg ?? ''}
                              onChange={e => setRowDraft(d => ({ ...d, gross_weight_kg: e.target.value === '' ? null : Number(e.target.value) }))}
                              className="w-20 px-2 py-1 border border-blue-400 rounded text-sm text-right bg-white dark:bg-gray-800"
                              placeholder="gross kg"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={rowDraft.total_amount ?? ''}
                            onChange={e => setRowDraft(d => ({ ...d, total_amount: e.target.value === '' ? null : Number(e.target.value) }))}
                            className="w-24 px-2 py-1 border border-blue-400 rounded text-sm text-right bg-white dark:bg-gray-800"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            value={rowDraft.country_of_origin ?? ''}
                            onChange={e => setRowDraft(d => ({ ...d, country_of_origin: e.target.value.toUpperCase().slice(0, 2) }))}
                            className="w-14 px-2 py-1 border border-blue-400 rounded text-sm text-center uppercase bg-white dark:bg-gray-800"
                            placeholder="CN"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button onClick={() => saveEditRow(item.id)} disabled={rowSaving} className="px-2 py-1 bg-blue-600 text-white rounded text-xs mr-1 disabled:opacity-50">
                            {rowSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button onClick={() => setEditingRow(null)} disabled={rowSaving} className="px-2 py-1 bg-gray-200 rounded text-xs">Cancel</button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-gray-500">{item.line_no ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">{item.description}</td>
                        <td className="px-4 py-3">
                          {editingHS === item.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                value={hsInput}
                                onChange={e => setHsInput(e.target.value)}
                                className="w-24 px-2 py-1 border border-blue-500 rounded font-mono text-sm"
                                placeholder="620342"
                                autoFocus
                              />
                              <button onClick={() => handleHSSave(item.id)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">✓</button>
                              <button onClick={() => setEditingHS(null)} className="px-2 py-1 bg-gray-200 rounded text-xs">✕</button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingHS(item.id); setHsInput(item.hs_code ?? '') }}
                              className={`font-mono text-sm px-2 py-0.5 rounded border ${
                                item.hs_code_confirmed
                                  ? 'border-green-300 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
                                  : item.hs_code
                                    ? 'border-yellow-300 text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                                    : 'border-gray-300 text-gray-400 dark:border-gray-600'
                              }`}
                            >
                              {item.hs_code ?? 'Add HS'}
                              {item.hs_code_confirmed && ' ✓'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {item.pcs != null ? `${item.pcs} pcs / ` : '—'}
                          {item.pcs != null && (
                            <span className="inline-flex items-center gap-1">
                              {item.ctns ?? 0} ctns
                              {item.ctns_original != null && (
                                <span
                                  className={item.ctns_original === 0 ? 'px-1 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 cursor-help' : 'text-amber-500 dark:text-amber-400 cursor-help'}
                                  title={item.ctns_original === 0 ? '原值为 0，已自动调为 1' : `Original ${item.ctns_original} rounded to ${item.ctns} for customs compliance`}
                                >
                                  ⚠
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">{item.gross_weight_kg != null ? `${Number(item.gross_weight_kg).toFixed(2)} kg` : '—'}</td>
                        <td className="px-4 py-3 text-right font-mono font-medium">{item.total_amount != null ? Number(item.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                        <td className="px-4 py-3 text-center text-xs text-gray-500">{item.country_of_origin ?? '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => startEditRow(item)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs">Edit</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 text-xs text-gray-500 flex gap-4">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 border border-green-300 rounded inline-block" /> Confirmed HS</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded inline-block" /> AI Suggested</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-white border border-gray-300 rounded inline-block" /> Missing</span>
            </div>
          </div>
        )}

        {/* CDS Export Settings */}
        {items.length > 0 && pivot.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">CDS Export Settings</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Configure export options</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Use CYUK DAN?</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setUseCyukDan(false)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        !useCyukDan
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      No
                    </button>
                    <button
                      onClick={() => setUseCyukDan(true)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        useCyukDan
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      Yes
                    </button>
                  </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {useCyukDan ? '33 columns with DAN codes' : '19 columns without DAN codes'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
